import React, { useEffect, useState } from 'react';
import { Layout, Breadcrumb } from 'antd';
import { useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useUpdateProject } from 'src/reactQuery/projects/mutations/useUpdateProject';
import { useProject } from 'src/reactQuery/projects/queries/useProject';
import { fetchLanguages } from 'src/actions/LanguagesAction';
import { useGetContentDraftMap } from 'src/reactQuery/Content/useContentDraftMap/useGetContentDraftMap';
import ContentDetailBody from './ContentDetailBody';
import InnerHeader from '../../component/InnerHeader';
import { fetchContent, fetchContentById, setContentDraft } from '../../actions/ContentAction';
import Loader from '../../component/Loader/Loader';
import { deleteContent, updateContent } from '../../services/napi';

import ArrowNextIcon from '../../asset/arrow-next.svg';

const ContentDetail = () => {
  const { contentId } = useParams();
  const { data: contentDraftMap } = useGetContentDraftMap();
  const dispatch = useDispatch();
  const { data: project, refetch } = useProject();
  const mutation = useUpdateProject({
    onSuccess: () => {
      refetch();
    }
  });
  const [isSaveEnabled, setSaveEnabled] = useState(true);

  const contentData = useSelector(({ content }) => content.contentStore[contentId]);

  useEffect(() => {
    async function load() {
      dispatch(setContentDraft({}));
      dispatch(fetchContentById(contentId, true));
      dispatch(fetchLanguages());
    }

    load();
  }, [contentId, dispatch]);

  const contentDraft = useSelector(({ content }) => content.contentDraft);
  const projectId = useSelector(({ project }) => project.currentProject._id);

  const setDraft = (content) => {
    dispatch(setContentDraft(content));
  };

  const [reRender, setRerender] = useState(false);

  const handleCallBack = (data) => {
    const newContent = contentDraft;
    if (data) {
      newContent.name = data;
      setDraft(newContent);
      setRerender(!reRender);
    }
  };

  if (!contentData || !Object.keys(contentDraft).length) {
    return <Loader />;
  }

  const onSave = async () => {
    const bodyContentDraft = {
      ...contentDraft,
      isReviewable: contentDraft.customType.isReviewable
    };
    delete bodyContentDraft.customType;
    setSaveEnabled(false);
    delete bodyContentDraft.data.undefined;

    if (contentDraftMap.length) {
      await Promise.all(
        contentDraftMap.map((content) => {
          return updateContent(content, content._id, content.project);
        })
      );
    }

    // save last time content was published
    if (bodyContentDraft.status === 'published' && project.metadata?.deployment) {
      const date = new Date().getTime();
      mutation.mutate({
        ...project,
        metadata: {
          ...project.metadata,
          deployment: {
            ...project.metadata.deployment,
            lastContentModifiedAt: date
          }
        }
      });
    }

    await updateContent(bodyContentDraft, bodyContentDraft._id, bodyContentDraft.project);
    setSaveEnabled(true);
    onBack();
  };

  const isPublished = contentDraft?.status === 'published';

  const toggleStatus = () => {
    setDraft({ ...contentDraft, status: isPublished ? 'draft' : 'published' });
  };

  async function removeContent() {
    await deleteContent(contentId, projectId);
    dispatch(fetchContent());
    window.history.back();
  }

  const actions = [
    { onClick: removeContent, title: 'Delete' },
    {
      disabled: !isSaveEnabled,
      onClick: toggleStatus,
      title: isPublished ? 'Switch to Draft' : 'Save As Draft',
      type: 'primary'
    },
    {
      disabled: !isSaveEnabled,
      onClick: onSave,
      title: 'Publish',
      type: 'primary'
    }
  ];

  const routes = [
    {
      breadcrumbName: 'home',
      path: '/content'
    },
    {
      breadcrumbName: contentDraft.name,
      path: `/${contentId}`
    }
  ];

  function itemRender(route, params, routes) {
    const last = routes.indexOf(route) === routes.length - 1;
    return last ? (
      <span>{route.breadcrumbName}</span>
    ) : (
      <Link to={route.path}>{route.breadcrumbName}</Link>
    );
  }

  const onBack = () => {
    window.history.back();
  };

  return (
    <Layout>
      <Breadcrumb
        itemRender={itemRender}
        routes={routes}
        separator={<img alt="next icon" src={ArrowNextIcon} />}
        style={{
          background: 'white',
          display: 'flex',
          justifyContent: 'flex-start',
          padding: '40px 0px 34px 35px'
        }}
      />

      <InnerHeader
        actions={actions}
        copyId={contentData.customType.api_id}
        onBack={onBack}
        parentCallBack={handleCallBack}
        subtitle=""
        title={contentDraft.name}
      />

      <ContentDetailBody contentDraft={contentDraft} setContentDraft={setDraft} />
    </Layout>
  );
};

export default ContentDetail;
